import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import { Logger } from '@atlas/core';
import { EmptyStateView, Label, Loader } from '@atlas/ui';

import {
  getSmarticoTournaments,
  useSmartico,
} from 'components/ClientProfile/Achievements/Smartico';

import { calculateTimeLeft, cupIcon, isTournamentSuitable, TabsList } from './shares';
import TournamentsCard from './TournamentsCard';

const TAB_SEARCH_KEY = 'category';
const TABS_DETAILS = [
  {
    key: ['is_upcoming', 'is_in_progress'],
    text: 'overview',
  },
  {
    key: 'is_user_registered',
    text: 'registered',
  },
  {
    key: 'is_in_progress',
    text: 'started',
  },
  {
    key: 'is_finished',
    text: 'finished',
  },
];

const SmarticoTournaments = () => {
  const { pathname, search } = useLocation();
  const { push, replace } = useHistory();
  const { isReadySmartico } = useSmartico();
  const [loading, setLoading] = useState(true);
  const [tournaments, setTournaments] = useState(null);
  const [timeLeftForTournaments, setTimeLeftForTournaments] = useState({});

  const filteredTabs = useMemo(() => {
    if (!tournaments) return [];
    return tournaments.reduce(
      (acc, tournament) =>
        TABS_DETAILS.reduce((arr, tab, index) => {
          const data = [...arr];
          data[index] = {
            ...data[index],
            count: (data[index].count || 0) + +isTournamentSuitable(tournament, tab.key),
          };
          return data;
        }, acc),
      TABS_DETAILS,
    );
  }, [tournaments]);

  const activeCategory = useMemo(() => {
    const params = new URLSearchParams(search);
    const activeCategoryId = params.get(TAB_SEARCH_KEY);
    if (!activeCategoryId) return TABS_DETAILS[0];
    const activeIndex = filteredTabs.findIndex(tabConfig => tabConfig.text === activeCategoryId);
    if (activeIndex < 1) return TABS_DETAILS[0];
    if (!filteredTabs[activeIndex]) return 'redirect';
    return TABS_DETAILS[activeIndex];
  }, [search, filteredTabs]);

  const filteredTournaments = useMemo(
    () => tournaments?.filter(tournament => isTournamentSuitable(tournament, activeCategory.key)),
    [activeCategory.key, tournaments],
  );

  useEffect(() => {
    if (activeCategory === 'redirect') {
      replace(pathname);
    }
  }, [activeCategory, pathname, replace]);

  const tournamentsUpdates = useCallback(tournaments => {
    Logger.debug('Smartico.tournaments', tournaments);
    setTournaments(tournaments);
    setLoading(false);
  }, []);

  useEffect(() => {
    if (isReadySmartico) {
      getSmarticoTournaments({
        cb: tournamentsUpdates,
        tournamentsUpdates,
      });
    }
  }, [isReadySmartico]);

  useEffect(() => {
    const updateTournamentTimes = () => {
      const updatedTimeLeft = {};
      tournaments?.forEach(tournament => {
        const targetTime = tournament.is_in_progress
          ? new Date(tournament.end_time).getTime()
          : new Date(tournament.start_time).getTime();
        updatedTimeLeft[tournament.instance_id] = calculateTimeLeft(targetTime);
      });
      setTimeLeftForTournaments(updatedTimeLeft);
    };

    const interval = setInterval(updateTournamentTimes, 1000);

    return () => clearInterval(interval);
  }, [tournaments]);

  const tabClickHandler = useCallback(
    ({ index, text }) => {
      if (index) {
        push(`?${TAB_SEARCH_KEY}=${text}`);
      } else {
        push(pathname);
      }
    },
    [pathname, push],
  );

  return (
    <div className="smartico-tournaments">
      {loading && (
        <div className="smartico-tournaments-loader">
          <Loader className="absolute" />
        </div>
      )}
      {!loading && (
        <div className="smartico-tournaments-container">
          <div className="smartico-tournaments-header">
            <img alt="cup" height={32} src={cupIcon} width={32} />
            <Label
              className="smartico-tournaments-header-title"
              message="smartico_tournaments_header_title"
            />
          </div>

          <TabsList
            categoryId={activeCategory.text}
            onClick={tabClickHandler}
            tabs={filteredTabs}
          />

          {!!filteredTournaments.length && activeCategory !== 'redirect' && (
            <div className="smartico-tournaments-cards">
              {filteredTournaments.map(tournament => (
                <TournamentsCard
                  key={tournament.instance_id}
                  pathname={pathname}
                  push={push}
                  timeLeftForTournaments={timeLeftForTournaments}
                  tournament={tournament}
                />
              ))}
            </div>
          )}

          {!filteredTournaments.length && (
            <EmptyStateView
              className="list-empty"
              description="smartico_tournaments_empty_description"
              title="smartico_tournaments_empty_title"
            />
          )}
        </div>
      )}
    </div>
  );
};

export default SmarticoTournaments;
